import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { CheckCircle, Lock, Heart } from "lucide-react";

const SHARE_PLATFORMS = [
  { id: "facebook", name: "Facebook", color: "#1877F2", icon: "f", url: (u, t) => `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}` },
  { id: "twitter", name: "X / Twitter", color: "#000", icon: "x", url: (u, t) => `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
  { id: "linkedin", name: "LinkedIn", color: "#0A66C2", icon: "in", url: (u, t) => `https://www.linkedin.com/shareArticle?mini=true&url=${u}&title=${t}` },
  { id: "whatsapp", name: "WhatsApp", color: "#25D366", icon: "wa", url: (u, t) => `https://wa.me/?text=${t}%20${u}` },
  { id: "telegram", name: "Telegram", color: "#26A5E4", icon: "tg", url: (u, t) => `https://t.me/share/url?url=${u}&text=${t}` },
  { id: "instagram", name: "Instagram", color: "#E4405F", icon: "ig", url: (u, t) => `https://www.instagram.com/` },
];

export function ShareToUnlock({ score, grade, url, onUnlocked }) {
  const [shared, setShared] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const shareUrl = encodeURIComponent(`https://toprankpulse.com/?url=${url}`);
  const shareText = encodeURIComponent(`My website scored ${score}/100 (Grade ${grade}) on TopRankPulse AI SEO Audit! Check yours for free:`);

  const handleShare = (platform) => {
    const p = SHARE_PLATFORMS.find(s => s.id === platform);
    if (!p) return;
    const shareLink = p.url(shareUrl, shareText);
    window.open(shareLink, "_blank", "width=600,height=400");
    setSelectedPlatform(platform);

    // Track share
    axios.post(`${API}/share/track`, null, { params: { platform, url } }).catch(() => {});

    setTimeout(() => {
      setShared(true);
      if (onUnlocked) onUnlocked();
    }, 3000);
  };

  if (shared) {
    return (
      <div className="bg-emerald-900/10 border border-emerald-800/50 p-6 text-center" data-testid="share-unlocked">
        <Heart className="w-8 h-8 text-[#a855f7] mx-auto mb-3 animate-pulse" />
        <p className="text-emerald-400 font-bold font-['Cabinet_Grotesk'] text-lg mb-1">Full report unlocked!</p>
        <p className="text-[var(--color-text-muted)] text-sm">Thank you for helping us grow. Every share brings us closer to making the web better for everyone.</p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] border border-[#FFE600]/30 p-6" data-testid="share-to-unlock">
      <div className="flex items-center gap-3 mb-4">
        <Lock className="w-5 h-5 text-[#FFE600]" />
        <div>
          <p className="text-white font-bold font-['Cabinet_Grotesk']">Share to Unlock Full Report</p>
          <p className="text-[var(--color-text-muted)] text-xs">Share your score and help others discover their AI visibility. You'll get the complete analysis with all recommendations.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="share-buttons">
        {SHARE_PLATFORMS.map(p => (
          <button
            key={p.id}
            onClick={() => handleShare(p.id)}
            className={`flex flex-col items-center gap-1.5 p-3 border transition-all ${
              selectedPlatform === p.id
                ? 'border-[#FFE600] bg-[#FFE600]/10'
                : 'border-[var(--color-border)] hover:border-[var(--color-primary)]'
            }`}
            data-testid={`share-${p.id}`}
          >
            <span className="w-9 h-9 flex items-center justify-center text-white font-bold text-xs rounded-sm" style={{backgroundColor: p.color}}>
              {p.icon}
            </span>
            <span className="text-[10px] text-[var(--color-text-muted)]">{p.name}</span>
          </button>
        ))}
      </div>

      {selectedPlatform && !shared && (
        <p className="text-[#FFE600] text-xs mt-3 text-center animate-pulse font-['JetBrains_Mono']">
          Verifying share... Please complete the share in the popup window.
        </p>
      )}

      <p className="text-zinc-700 text-[10px] text-center mt-4 font-['JetBrains_Mono']">
        By sharing, you help us grow and keep this tool free for everyone.
      </p>
    </div>
  );
}
